import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { PublicLayout } from '../components/landing/PublicLayout';
import {
  Calendar, Users, Map, Swords, FileText, BookOpen,
  Dice5, Music, GitFork, Store, Settings, Gem, Target,
  ChevronRight,
} from 'lucide-react';

import { CombatTrackerMockup } from '../components/landing/mockups/CombatTrackerMockup';
import { MapMockup } from '../components/landing/mockups/MapMockup';
import { CharacterSheetMockup } from '../components/landing/mockups/CharacterSheetMockup';
import { DiceRollerMockup } from '../components/landing/mockups/DiceRollerMockup';
import { LoreWikiMockup } from '../components/landing/mockups/LoreWikiMockup';
import { QuestMockup } from '../components/landing/mockups/QuestMockup';
import { AudioPlayerMockup } from '../components/landing/mockups/AudioPlayerMockup';
import { EquipmentCardsMockup } from '../components/landing/mockups/EquipmentCardsMockup';
import { MarketplaceMockup } from '../components/landing/mockups/MarketplaceMockup';
import { GameSystemBuilderMockup } from '../components/landing/mockups/GameSystemBuilderMockup';

export default function FeaturesPage() {
  const { isAuthenticated, language } = useAuth();
  const navigate = useNavigate();

  const categories = [
    {
      title: t('features_category_campaign', language),
      description: t('features_category_campaign_desc', language),
      features: [
        { icon: Calendar, title: t('landing_feature_1_title', language), desc: t('landing_feature_1_desc', language), color: 'text-blue-400', bg: 'bg-blue-500/10' },
        { icon: Target, title: language === 'fr' ? 'Quêtes avec Actes' : 'Quests with Acts', desc: language === 'fr' ? 'Structurez vos intrigues en chapitres avec objectifs et progression.' : 'Structure your plots into chapters with objectives and progression.', color: 'text-green-400', bg: 'bg-green-500/10', mockup: <QuestMockup /> },
        { icon: Users, title: t('landing_feature_2_title', language), desc: t('landing_feature_2_desc', language), color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
      ],
    },
    {
      title: t('features_category_combat', language),
      description: t('features_category_combat_desc', language),
      features: [
        { icon: Swords, title: t('landing_feature_5_title', language), desc: t('landing_feature_5_desc', language), color: 'text-red-400', bg: 'bg-red-500/10', mockup: <CombatTrackerMockup /> },
        { icon: Map, title: t('landing_feature_3_title', language), desc: t('landing_feature_7_desc', language), color: 'text-amber-400', bg: 'bg-amber-500/10', mockup: <MapMockup /> },
        { icon: Dice5, title: t('landing_feature_9_title', language), desc: t('landing_feature_9_desc', language), color: 'text-cyan-400', bg: 'bg-cyan-500/10', mockup: <DiceRollerMockup /> },
      ],
    },
    {
      title: t('features_category_world', language),
      description: t('features_category_world_desc', language),
      features: [
        { icon: BookOpen, title: t('landing_feature_8_title', language), desc: t('landing_feature_8_desc', language), color: 'text-violet-400', bg: 'bg-violet-500/10', mockup: <LoreWikiMockup /> },
        { icon: GitFork, title: t('landing_feature_11_title', language), desc: t('landing_feature_11_desc', language), color: 'text-teal-400', bg: 'bg-teal-500/10' },
        { icon: FileText, title: t('landing_feature_6_title', language), desc: t('landing_feature_6_desc', language), color: 'text-orange-400', bg: 'bg-orange-500/10', mockup: <CharacterSheetMockup /> },
      ],
    },
    {
      title: t('features_category_assets', language),
      description: t('features_category_assets_desc', language),
      features: [
        { icon: Music, title: t('landing_feature_10_title', language), desc: t('landing_feature_10_desc', language), color: 'text-pink-400', bg: 'bg-pink-500/10', mockup: <AudioPlayerMockup /> },
        { icon: Gem, title: t('landing_feature_4_title', language), desc: t('landing_feature_4_desc', language), color: 'text-purple-400', bg: 'bg-purple-500/10', mockup: <EquipmentCardsMockup /> },
      ],
    },
    {
      title: t('features_category_platform', language),
      description: t('features_category_platform_desc', language),
      features: [
        { icon: Store, title: t('landing_feature_12_title', language), desc: t('landing_feature_12_desc', language), color: 'text-indigo-400', bg: 'bg-indigo-500/10', mockup: <MarketplaceMockup /> },
        { icon: Settings, title: t('landing_feature_13_title', language), desc: t('landing_feature_13_desc', language), color: 'text-rose-400', bg: 'bg-rose-500/10', mockup: <GameSystemBuilderMockup /> },
      ],
    },
  ];

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-16 sm:py-24 px-4 text-center relative">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background dark:from-primary/8" />
        <div className="relative z-10 max-w-3xl mx-auto">
          <h1 className="font-cinzel text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            {t('features_page_title', language)}
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto">
            {t('features_page_subtitle', language)}
          </p>
        </div>
      </section>

      {/* Feature categories */}
      {categories.map((category, ci) => (
        <section
          key={ci}
          className={`py-16 sm:py-20 px-4 ${ci % 2 === 0 ? '' : 'bg-secondary/10'}`}
        >
          <div className="max-w-6xl mx-auto">
            <div className="mb-10">
              <h2 className="font-cinzel text-2xl sm:text-3xl font-bold mb-2">{category.title}</h2>
              <p className="text-muted-foreground">{category.description}</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {category.features.map((feature, fi) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={fi}
                    className={`rounded-xl border border-border/50 bg-card p-5 sm:p-6 hover:border-primary/20 transition-colors ${
                      !feature.mockup && category.features.length === 3 && fi === 2 ? 'lg:col-span-2 lg:max-w-lg' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`p-2.5 rounded-xl ${feature.bg} ${feature.color}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="font-cinzel font-bold text-base">{feature.title}</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">{feature.desc}</p>
                      </div>
                    </div>
                    {feature.mockup && feature.mockup}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ))}

      {/* Bottom CTA */}
      <section className="py-20 sm:py-28 px-4 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-cinzel text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
            {t('features_cta', language)}
          </h2>
          <p className="text-muted-foreground mb-8">{t('features_cta_desc', language)}</p>
          <Button
            size="lg"
            className="fantasy-button text-lg px-10 py-7 group"
            onClick={() => navigate(isAuthenticated ? '/dashboard' : '/register')}
          >
            {t('landing_cta', language)}
            <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>
    </PublicLayout>
  );
}
